import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { message, Spin, Progress } from 'antd';
import { 
  UserOutlined, 
  FileTextOutlined, 
  ShoppingCartOutlined, 
  TeamOutlined, 
  BellOutlined, 
  FileOutlined,
  RiseOutlined,
  FallOutlined,
  DashboardOutlined,
  FormOutlined,
  ProfileOutlined,
  AuditOutlined,
  UsergroupAddOutlined,
  BankOutlined
} from '@ant-design/icons';

interface StatData {
  total: number;
  growth: number;
}

interface DashboardStats {
  users: StatData;
  members: StatData;
  experts: StatData;
  purchasers: StatData;
  bids: StatData;
  orders: StatData;
  announcements: StatData;
  invoices: StatData;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/dashboard/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json());

      if (resp && resp.ok) {
        setStats(resp.data);
      } else {
        message.error(resp?.error || '获取统计数据失败');
      }
    } catch (err) {
      message.error('获取统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  const formatGrowth = (growth: number) => {
    if (growth > 0) return `+${growth}%`;
    if (growth < 0) return `${growth}%`;
    return '0%';
  };

  const statCards = stats ? [
    { key: 'users', label: '后台管理人员数量', value: stats.users?.total || 0, icon: UserOutlined, color: 'blue', growth: stats.users?.growth || 0 },
    { key: 'members', label: '供应商数量', value: stats.members?.total || 0, icon: TeamOutlined, color: 'cyan', growth: stats.members?.growth || 0 },
    { key: 'experts', label: '评审专家数量', value: stats.experts?.total || 0, icon: UsergroupAddOutlined, color: 'geekblue', growth: stats.experts?.growth || 0 },
    { key: 'purchasers', label: '采购人数量', value: stats.purchasers?.total || 0, icon: BankOutlined, color: 'gold', growth: stats.purchasers?.growth || 0 },
    { key: 'bids', label: '标书总数', value: stats.bids?.total || 0, icon: FileTextOutlined, color: 'green', growth: stats.bids?.growth || 0 },
    { key: 'orders', label: '订单总数', value: stats.orders?.total || 0, icon: ShoppingCartOutlined, color: 'orange', growth: stats.orders?.growth || 0 },
    { key: 'announcements', label: '公告总数', value: stats.announcements?.total || 0, icon: BellOutlined, color: 'purple', growth: stats.announcements?.growth || 0 },
    { key: 'invoices', label: '发票总数', value: stats.invoices?.total || 0, icon: FileOutlined, color: 'pink', growth: stats.invoices?.growth || 0 },
  ] : [];

  // 快捷操作按钮
  const quickActions = [
    { icon: FormOutlined, label: '招标公告', color: '#3b82f6', path: '/admin/bid-tender' },
    { icon: ShoppingCartOutlined, label: '订单中心', color: '#10b981', path: '/admin/order-management' },
    { icon: ProfileOutlined, label: '公司动态', color: '#f59e0b', path: '/admin/info-company' },
    { icon: AuditOutlined, label: '供应商审核', color: '#8b5cf6', path: '/admin/account-supplier-audit' },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* 欢迎卡片 */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #0d2137 100%)',
        borderRadius: 16,
        padding: '32px 40px',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: -60,
          right: -40,
          width: 250,
          height: 250,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ color: 'white', fontSize: 26, fontWeight: 600, marginBottom: 8 }}>
            欢迎回来，管理员！
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, margin: 0 }}>
            这是您的管理控制台，可以在这里查看系统运行状态
          </p>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 12,
            padding: '16px 24px',
            textAlign: 'center'
          }}>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginBottom: 4 }}>本月订单</div>
            <div style={{ color: 'white', fontSize: 24, fontWeight: 700 }}>{stats?.orders?.total || 0}</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            borderRadius: 12,
            padding: '16px 24px',
            textAlign: 'center',
            boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)'
          }}>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, marginBottom: 4 }}>订单增长率</div>
            <div style={{ color: 'white', fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
              {(stats?.orders?.growth || 0) >= 0 ? <RiseOutlined /> : <FallOutlined />}
              {formatGrowth(stats?.orders?.growth || 0)}
            </div>
          </div>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="dashboard-stats">
        {statCards.map(card => (
          <div key={card.key} className="stat-card">
            <div className={`stat-card-icon ${card.color}`}>
              <card.icon />
            </div>
            <div className="stat-card-info">
              <div className="stat-card-value">{card.value}</div>
              <div className="stat-card-label">{card.label}</div>
            </div>
            <div style={{
              background: card.growth >= 0 ? '#ecfdf5' : '#fef2f2',
              color: card.growth >= 0 ? '#059669' : '#dc2626',
              padding: '4px 10px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              {card.growth >= 0 ? <RiseOutlined style={{ fontSize: 10 }} /> : <FallOutlined style={{ fontSize: 10 }} />}
              {formatGrowth(card.growth)}
            </div>
          </div>
        ))}
      </div>

      {/* 底部信息卡片 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 24 }}>
        {/* 系统状态 */}
        <div style={{
          background: 'white',
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <DashboardOutlined style={{ color: 'white', fontSize: 20 }} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>系统状态</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>所有服务运行正常</div>
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 14, color: '#6b7280' }}>服务器负载</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#1f2937' }}>45%</span>
              </div>
              <Progress percent={45} showInfo={false} strokeColor="#10b981" />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 14, color: '#6b7280' }}>内存使用</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#1f2937' }}>62%</span>
              </div>
              <Progress percent={62} showInfo={false} strokeColor="#3b82f6" />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 14, color: '#6b7280' }}>存储空间</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#1f2937' }}>38%</span>
              </div>
              <Progress percent={38} showInfo={false} strokeColor="#8b5cf6" />
            </div>
          </div>
        </div>

        {/* 快捷操作 */}
        <div style={{
          background: 'white',
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
        }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 20 }}>
            快捷操作
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            {quickActions.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 16,
                  background: '#f8fafc',
                  borderRadius: 12,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => navigate(item.path)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: `${item.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <item.icon style={{ color: item.color, fontSize: 18 }} />
                </div>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
